import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground shadow hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80",
        outline: "text-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

/**
 * Props for the Badge component
 */
export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

/**
 * Badge component displays a small label or status indicator with customizable variants.
 * Supports different visual styles (default, secondary, destructive, outline).
 * 
 * @component
 * @param {BadgeProps} props - Component props
 * @param {"default" | "secondary" | "destructive" | "outline"} [props.variant="default"] - Visual variant of the badge
 * @param {string} [props.className] - Additional CSS classes
 * @param {React.HTMLAttributes<HTMLDivElement>} props - All standard div element props
 * @returns {JSX.Element} A styled badge element
 * 
 * @example
 * ```tsx
 * <Badge variant="default">New</Badge>
 * ```
 * 
 * @example
 * ```tsx
 * <Badge variant="destructive">Error</Badge>
 * ```
 */
function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  )
}

export { Badge, badgeVariants }
